/**
 * The web co-seller mic (D6, 2026-10-05): a user who joins someone's desktop
 * call "with my mic" streams their own microphone to the transcription proxy
 * as `salesperson_<n>`, on exactly the desktop apps' wire contract
 * (salesguru-workspace REALTIME_ARCHITECTURE.md §10; services
 * `deepgram_proxy_service.py`). Only the mic is sent: the owner's app captures
 * the customer audio.
 */

/**
 * The proxy's echo proof (`echo_audio.envelope_of`) assumes 16 kHz, so the web
 * MUST send 16 kHz mono Int16 little-endian (`linear16`), whatever rate the
 * browser captures at.
 */
export const COSELLER_SAMPLE_RATE = 16_000;
export const COSELLER_CHANNELS = 1;
export const COSELLER_ENCODING = 'linear16';
/** One binary frame: 100 ms = 1600 samples = 3200 bytes. */
export const COSELLER_FRAME_MS = 100;
export const MS_PER_SECOND = 1000;
export const COSELLER_FRAME_SAMPLES = (COSELLER_SAMPLE_RATE * COSELLER_FRAME_MS) / MS_PER_SECOND;
export const PCM16_BYTES_PER_SAMPLE = 2;
/** C2 `audio_ms` = bytes already sent on this socket / 32 (16 kHz mono Int16). */
export const PCM16_BYTES_PER_MS = (COSELLER_SAMPLE_RATE * PCM16_BYTES_PER_SAMPLE) / MS_PER_SECOND;

/** A `capture_stamp` goes out before the first frame, then after at least this much audio. */
export const CAPTURE_STAMP_INTERVAL_MS = 1000;
/**
 * The capture clock keeps its EARLIEST estimate of the stream's start over
 * this much recent audio: the main thread only ever receives a block late
 * (never early), so the minimum filters out its scheduling jitter.
 */
export const CAPTURE_CLOCK_WINDOW_MS = 2000;
/**
 * An estimate this much later than the clock means samples never arrived (the
 * capture stalled): the clock re-anchors and the next frame carries a stamp.
 */
export const CAPTURE_GAP_MS = 200;

/** The welcome's `clockSync` value when the proxy speaks the clock protocol (the URL sent `clock=1`). */
export const CLOCK_SYNC_VERSION = 1;

/** The participant id the session's creator streams as; every co-seller's is `salesperson_<n>`. */
export const CREATOR_PARTICIPANT_ID = 'salesperson';

/** Frame types on the audio socket (C2). */
export const COSELLER_MIC_FRAME = {
  welcome: 'connection_established',
  clockPing: 'clock_ping',
  clockPong: 'clock_pong',
  captureStamp: 'capture_stamp',
  transcript: 'transcript',
  error: 'error',
} as const;

/** The AudioWorklet processor that forwards raw mic blocks to the main thread. */
export const COSELLER_CAPTURE_PROCESSOR = 'coseller-mic-capture';
